/**
 * Human-readable labels for tool invocations.
 *
 * Pure string logic (no React) so the same narration can be reused by other
 * Meridian surfaces (e.g. Slack status lines) without pulling in components.
 *
 * A tool name like `search_contacts` becomes:
 *   running:  "Searching contacts"      + subject → "Searching contacts · ACME"
 *   done:     "Searched contacts"
 * Unknown verbs fall back to a humanized tool name ("Sync quickbooks" →
 * "Running sync quickbooks…" / "Ran sync quickbooks").
 */

/** Present-participle + past-tense forms for the leading verb of a tool name. */
const VERB_FORMS: Record<string, { running: string; done: string }> = {
  search: { running: 'Searching', done: 'Searched' },
  find: { running: 'Finding', done: 'Found' },
  list: { running: 'Loading', done: 'Loaded' },
  get: { running: 'Fetching', done: 'Fetched' },
  query: { running: 'Querying', done: 'Queried' },
  create: { running: 'Creating', done: 'Created' },
  update: { running: 'Updating', done: 'Updated' },
  edit: { running: 'Updating', done: 'Updated' },
  delete: { running: 'Deleting', done: 'Deleted' },
  remove: { running: 'Removing', done: 'Removed' },
  add: { running: 'Adding', done: 'Added' },
  send: { running: 'Sending', done: 'Sent' },
  resend: { running: 'Resending', done: 'Resent' },
  draft: { running: 'Drafting', done: 'Drafted' },
  generate: { running: 'Generating', done: 'Generated' },
  summarize: { running: 'Summarizing', done: 'Summarized' },
  enrich: { running: 'Enriching', done: 'Enriched' },
  extract: { running: 'Extracting', done: 'Extracted' },
  qualify: { running: 'Qualifying', done: 'Qualified' },
  enroll: { running: 'Enrolling', done: 'Enrolled' },
  schedule: { running: 'Scheduling', done: 'Scheduled' },
  cancel: { running: 'Cancelling', done: 'Cancelled' },
  merge: { running: 'Merging', done: 'Merged' },
  log: { running: 'Logging', done: 'Logged' },
  read: { running: 'Reading', done: 'Read' },
  browse: { running: 'Browsing', done: 'Browsed' },
  download: { running: 'Downloading', done: 'Downloaded' },
  copy: { running: 'Copying', done: 'Copied' },
  mark: { running: 'Marking', done: 'Marked' },
  set: { running: 'Setting', done: 'Set' },
  link: { running: 'Linking', done: 'Linked' },
  void: { running: 'Voiding', done: 'Voided' },
  publish: { running: 'Publishing', done: 'Published' },
  pause: { running: 'Pausing', done: 'Paused' },
  resume: { running: 'Resuming', done: 'Resumed' },
  snooze: { running: 'Snoozing', done: 'Snoozed' },
  bail: { running: 'Stopping', done: 'Stopped' },
  advance: { running: 'Advancing', done: 'Advanced' },
  export: { running: 'Exporting', done: 'Exported' },
  apply: { running: 'Applying', done: 'Applied' },
  rename: { running: 'Renaming', done: 'Renamed' },
  reorder: { running: 'Reordering', done: 'Reordered' },
  withdraw: { running: 'Withdrawing', done: 'Withdrew' },
  block: { running: 'Blocking', done: 'Blocked' },
  unblock: { running: 'Unblocking', done: 'Unblocked' },
  grant: { running: 'Granting', done: 'Granted' },
  manage: { running: 'Managing', done: 'Managed' },
  recommend: { running: 'Recommending', done: 'Recommended' },
  attribute: { running: 'Attributing', done: 'Attributed' },
  approve: { running: 'Reviewing', done: 'Reviewed' },
  scope: { running: 'Scoping', done: 'Scoped' },
  activate: { running: 'Activating', done: 'Activated' },
  recall: { running: 'Recalling', done: 'Recalled' },
  remember: { running: 'Saving', done: 'Saved' },
  forget: { running: 'Forgetting', done: 'Forgot' },
};

/**
 * Full-label overrides for tools whose generated label reads poorly.
 * Value is the OBJECT phrase; the verb forms still come from VERB_FORMS
 * unless `running`/`done` are given explicitly.
 */
const TOOL_LABEL_OVERRIDES: Record<string, { running: string; done: string }> = {
  web_search: { running: 'Searching the web', done: 'Searched the web' },
  // Anthropic server-side sandbox. "Running code" reads as the assistant doing
  // arithmetic/analysis, which is what it is — not as us executing on our infra.
  code_execution: { running: 'Running code', done: 'Ran code' },
  search_crm: { running: 'Searching the CRM', done: 'Searched the CRM' },
  account_brief: { running: 'Preparing account brief', done: 'Prepared account brief' },
  update_plan: { running: 'Updating plan', done: 'Updated plan' },
  scope_and_price: { running: 'Scoping and pricing', done: 'Scoped and priced' },
};

/** Input keys checked (in order) for a human-meaningful subject to append. */
const SUBJECT_KEYS = [
  'query',
  'search',
  'searchTerm',
  'name',
  'title',
  'subject',
  'company_name',
  'companyName',
  'contact_name',
  'contactName',
  'email',
  'domain',
  'stage',
] as const;

const SUBJECT_MAX_CHARS = 48;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** First human-meaningful string arg, for "Searching contacts · ACME". */
export function getToolSubject(input: unknown): string | null {
  if (!isRecord(input)) return null;
  for (const key of SUBJECT_KEYS) {
    const value = input[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return truncate(value.trim(), SUBJECT_MAX_CHARS);
    }
  }
  // first_name + last_name is a common pair on contact tools
  const first = input.first_name ?? input.firstName;
  const last = input.last_name ?? input.lastName;
  if (typeof first === 'string' && first.trim()) {
    const full = typeof last === 'string' && last.trim() ? `${first.trim()} ${last.trim()}` : first.trim();
    return truncate(full, SUBJECT_MAX_CHARS);
  }
  return null;
}

/** "quickbooks_invoice" → "quickbooks invoice" */
function humanizeWords(words: string[]): string {
  return words.join(' ').replace(/-/g, ' ');
}

export interface ToolLabel {
  /** Present-progressive phrase, no trailing ellipsis: "Searching contacts" */
  running: string;
  /** Past-tense phrase: "Searched contacts" */
  done: string;
  /** Optional human subject pulled from the input args. */
  subject: string | null;
}

/**
 * Verb-phrase label for a tool invocation. Falls back to a humanized tool
 * name for unknown verbs so MCP/long-tail tools degrade gracefully.
 */
export function getToolLabel(toolName: string, input?: unknown): ToolLabel {
  const subject = getToolSubject(input);
  const override = TOOL_LABEL_OVERRIDES[toolName];
  if (override) {
    return { ...override, subject };
  }

  const words = toolName.split('_').filter((w) => w.length > 0);
  const verb = words[0]?.toLowerCase() ?? '';
  const forms = VERB_FORMS[verb];
  if (forms && words.length > 1) {
    const object = humanizeWords(words.slice(1));
    return {
      running: `${forms.running} ${object}`,
      done: `${forms.done} ${object}`,
      subject,
    };
  }

  const humanized = humanizeWords(words) || 'tool';
  return {
    running: `Running ${humanized}`,
    done: `Ran ${humanized}`,
    subject,
  };
}

/** One-line streaming-indicator label: "Searching contacts · ACME…" */
export function getToolRunningLine(toolName: string, input?: unknown): string {
  const label = getToolLabel(toolName, input);
  return label.subject ? `${label.running} · ${label.subject}…` : `${label.running}…`;
}
